import React, {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  ILinkSubcontractorTableContext,
  LinkSubcontractorTableContext,
} from './LinkSubcontractorTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { LinkSubcontractorContext } from '../LinkSubcontractor.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyLinkedContractors } from './EmptyLinkedContractors'
import tw from 'twin.macro'
import { LinkSubcontractorActionMenu } from './LinkSubcontractorActionMenu'
import { LinkSubcontractorHeader } from './header/LinkSubcontractorHeader'
import {
  linkSubcontractorColumns,
  linkSubcontractorPageSizeOptions,
} from 'src/constants/linkSubcontractor/linkSubcontractorGrid'
import { FetchContractorsPerCustomer } from 'src/constants/actionTypes'
import { LinkedSubcontractor } from 'src/interfaces/subcontractor'
import { SubcontractorRequestStatus } from 'src/constants/subcontractorRequests/enum'

interface FetchContractorsPerCustomerParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  statusFilter: Array<SubcontractorRequestStatus>
  sortModel: GridSortModel
  selectedMCCustomerId: string
}

interface Props {
  overlay?: ReactNode
}

const getRowId = (row: LinkedSubcontractor) => row.id

const LinkSubcontractorTableComponent = ({ overlay }: Props) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery, selectedMCCustomerId } = useContext(LinkSubcontractorContext)

  const { paginationModel, limit, offset, setPaginationModel } = useGridPagination(
    linkSubcontractorPageSizeOptions,
  )

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [contractors, setContractors] = useState<Array<LinkedSubcontractor>>([])
  const [selectedContractorIds, setSelectedContractorIds] = useState<GridRowSelectionModel>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)

  const [contractorToAction, setContractorToAction] = useState<LinkedSubcontractor | null>(null)
  const [totalContractors, setTotalContractors] = useState<number>(0)
  const [statusFilter, setStatusFilter] = useState<Array<SubcontractorRequestStatus>>([])
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'name',
      sort: 'desc',
    },
  ])

  const fetchContractorsPerCustomer = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      statusFilter,
      selectedMCCustomerId,
    }: FetchContractorsPerCustomerParams) => {
      if (!selectedMCCustomerId) {
        return null
      }

      setSelectedContractorIds([])

      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          contractorStatus: statusFilter,
          customerId: selectedMCCustomerId,
        }

        if (searchQuery) {
          params.contractorSearch = searchQuery
        }

        if (selectedMarket?.id) {
          params.partnerMarketId = selectedMarket?.id
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<LinkedSubcontractor>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchContractorsPerCustomer.request(params))

        setContractors(payload.data)
        setTotalContractors(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id, selectedMCCustomerId],
  )

  const refreshContractors = useCallback(async () => {
    await fetchContractorsPerCustomer({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      statusFilter,
      selectedMCCustomerId,
    })
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    statusFilter,
    fetchContractorsPerCustomer,
    selectedMCCustomerId,
  ])

  useEffect(() => {
    fetchContractorsPerCustomer({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      statusFilter,
      selectedMCCustomerId,
    })
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    statusFilter,
    selectedMarket?.id,
    selectedMCCustomerId,
  ])

  const handleRowSelectionChange = useCallback((newSelectionModel: GridRowSelectionModel) => {
    setSelectedContractorIds(newSelectionModel)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const openLinkSubcontractorItemToAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, contractorToAction: LinkedSubcontractor) => {
      setActionAnchor(event.currentTarget)
      setContractorToAction(contractorToAction)
    },
    [],
  )

  const closeLinkSubcontractorItemToAction = useCallback(() => {
    setActionAnchor(null)
  }, [])

  const clearLinkSubcontractorItemToAction = useCallback(() => {
    setContractorToAction(null)
  }, [])

  const linkSubcontractorContextValue = useMemo<ILinkSubcontractorTableContext>(() => {
    return {
      statusFilter,
      setStatusFilter,

      tagsFilter,
      setTagsFilter,

      refreshContractors,

      actionAnchor,
      contractorToAction,
      openLinkSubcontractorItemToAction,
      closeLinkSubcontractorItemToAction,
      clearLinkSubcontractorItemToAction,

      selectedContractorIds,
    }
  }, [
    statusFilter,
    setStatusFilter,

    tagsFilter,
    setTagsFilter,

    refreshContractors,

    actionAnchor,
    contractorToAction,
    openLinkSubcontractorItemToAction,
    closeLinkSubcontractorItemToAction,
    clearLinkSubcontractorItemToAction,

    selectedContractorIds,
  ])

  return (
    <LinkSubcontractorTableContext.Provider value={linkSubcontractorContextValue}>
      <div css={tw`relative`}>
        <LinkSubcontractorHeader
          refreshLinkedContractors={refreshContractors}
          contractors={contractors}
        />
        <GridTable<LinkedSubcontractor>
          rows={contractors}
          isLoading={isLoading}
          columns={linkSubcontractorColumns}
          paginationModel={paginationModel}
          pageSizeOptions={linkSubcontractorPageSizeOptions}
          rowCount={totalContractors}
          sortModel={sortModel}
          className={[
            {
              '@media (min-height: 830px)': tw`h-[590px]`,
              '@media (min-height: 1000px)': tw`h-[770px]`,
              '@media (min-height: 1440px)': tw`h-[1170px]`,
              '@media (min-height: 2000px)': tw`h-[1570px]`,
            },
          ]}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={handleChangePagination}
          getRowId={getRowId}
          EmptyElement={!overlay ? EmptyLinkedContractors : undefined}
          onRowSelectionModelChange={handleRowSelectionChange}
          rowSelectionModel={selectedContractorIds}
          checkboxSelection
        />
        <LinkSubcontractorActionMenu />
        {overlay}
      </div>
    </LinkSubcontractorTableContext.Provider>
  )
}

export const LinkSubcontractorTable = memo(LinkSubcontractorTableComponent)
